import React, { useState, useEffect } from 'react';
import { Player } from '../types';
import { db, collection, getDocs, query, where } from '../firebaseConfig';
import { isPedagogicalStaff, isStaff } from '../utils/roles'; // PRESENCE-PRIVACY-V1
import { checkAccessFor, type AccessContext } from '../utils/access';

interface OccupantListProps {
  occupants: Player[];
  classroomId?: string;
  classroomIds?: string[];
  roomId?: string;
  accessCtx?: AccessContext;
  currentUser: Player;
  onPlayerClick: (player: Player) => void;
  onClose: () => void;
}

const s = (val: any): string => {
  if (typeof val === 'string') return val;
  if (!val) return '';
  if (typeof val === 'object') return val.da || val.en || Object.values(val).find(v => typeof v === 'string') as string || '';
  return String(val);
};

const OccupantList: React.FC<OccupantListProps> = ({ occupants, classroomId, classroomIds, roomId, currentUser, accessCtx, onPlayerClick, onClose }) => {
  const [allRoomMembers, setAllRoomMembers] = useState<Player[]>([]);
  const [roomData, setRoomData] = useState<any>(null);
  // PRESENCE-PRIVACY-V1 (21-9-2026): presence har ikke laengere rigtige navne og pakke. Staff henter dem fra users.
  const viewerIsStaff = isStaff(currentUser.role);
  const [staffInfo, setStaffInfo] = useState<Record<string, any>>({});
  const occupantKey = occupants.map((o) => o.id).sort().join(',');
  useEffect(() => {
    if (viewerIsStaff === false || occupantKey === '') return;
    let alive = true;
    (async () => {
      const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
      const next: Record<string, any> = {};
      await Promise.all(occupantKey.split(',').map(async (id) => {
        try { const snap = await getDoc(firestoreDoc(db, 'users', id)); if (snap.exists()) next[id] = snap.data(); } catch (e) { /* slettet eller ingen adgang */ }
      }));
      if (alive) setStaffInfo(next);
    })();
    return () => { alive = false; };
  }, [viewerIsStaff, occupantKey]);

  useEffect(() => {
    const effClassroomIds: string[] = (classroomIds && classroomIds.length > 0)
      ? classroomIds
      : (classroomId ? [classroomId] : []);
    if (effClassroomIds.length === 0 && !roomId) return;
    const loadMembers = async () => {
      const members: Player[] = [];
      // Hent elever via classIds — array-contains-any over alle linkede classrooms
      if (effClassroomIds.length > 0) {
        // Firestore array-contains-any har max 30 vaerdier; trimmer hvis flere
        const idsForQuery = effClassroomIds.slice(0, 30);
        const studentQ = query(collection(db, 'users'), where('classIds', 'array-contains-any', idsForQuery));
        const studentSnap = await getDocs(studentQ);
        studentSnap.forEach(d => {
          const data = d.data() as any;
          if (!members.find(m => m.id === d.id)) {
            members.push({ ...data, id: d.id, wizardName: data.username || '', realName: data.realName || '' } as Player);
          }
        });
      }

      // Hent rum-data for gæstevogtere og synlige admins
      const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
      
      // Hent roomId fra props hvis tilgængeligt - ellers spring over
      const roomSnap = roomId ? await getDoc(firestoreDoc(db, 'rooms', roomId)) : null;
      const rData = roomSnap?.exists() ? roomSnap.data() : null;
      setRoomData(rData);
      
      // Gæstevogtere og synlige admins fra rum
      const extraIds: string[] = [
        ...(rData?.guestGuardianIds || []),
        ...(rData?.visibleAdminIds || [])
      ];
      for (const eid of extraIds) {
        if (!members.find(m => m.id === eid)) {
          const eDoc = await getDoc(firestoreDoc(db, 'users', eid));
          if (eDoc.exists()) {
            const data = eDoc.data() as any;
            members.push({ id: eDoc.id, ...data, wizardName: data.username || '', realName: data.realName || '' } as Player);
          }
        }
      }

      for (const _cId of effClassroomIds) {
      const clsDoc = await getDoc(firestoreDoc(db, 'classrooms', _cId));
      if (clsDoc.exists()) {
        const clsData = clsDoc.data() as any;
        // Hall-niveau vogtere (kun role: guardian)
        const guardianIds: string[] = clsData.guardianIds || (clsData.primaryGuardianId ? [clsData.primaryGuardianId] : []);
        
        // Phase 3: Fagdørs-vogtere laeses fra flat subjects collection
        // (classroom.subjects er deprecated og toemmes i Step 5 - vi laeser fra top-level subjects filtered on classroomId)
        const subjectGuardianIds: string[] = [];
        try {
          const subjSnap = await getDocs(query(collection(db, 'subjects'), where('classroomId', '==', _cId)));
          subjSnap.docs.forEach(d => {
            const s = d.data() as any;
            if (s.primaryGuardianId) subjectGuardianIds.push(s.primaryGuardianId);
            (s.assistantTeacherIds || []).forEach((id: string) => subjectGuardianIds.push(id));
          });
        } catch (e) {
          // Fallback til gammel struktur hvis flat collection ikke er populated endnu
          (clsData.subjects || []).forEach((s: any) => {
            if (s.primaryGuardianId) subjectGuardianIds.push(s.primaryGuardianId);
            (s.assistantTeacherIds || []).forEach((id: string) => subjectGuardianIds.push(id));
          });
        }
        
        const allGuardianIds = [...new Set([...guardianIds, ...subjectGuardianIds])];
        for (const gid of allGuardianIds) {
          if (!members.find(m => m.id === gid)) {
            const gDoc = await getDoc(firestoreDoc(db, 'users', gid));
            if (gDoc.exists()) {
              const data = gDoc.data() as any;
              // Vis kun role: guardian — ikke admin/superadmin
              if (data.role === 'guardian') {
                members.push({ id: gDoc.id, ...data, wizardName: data.username || '', realName: data.realName || '' } as Player);
              }
            }
          }
        }
      }
      } // end for (const _cId of effClassroomIds)
      // Filtrer: kun medlemmer der faktisk har adgang til DETTE rum
      let visibleMembers = members;
      if (accessCtx && roomId) {
        const guestIds = new Set([
          ...(rData?.guestGuardianIds || []),
          ...(rData?.visibleAdminIds || [])
        ]);
        visibleMembers = members.filter(m => {
          // Eksplicit listede gaestevogtere/admins har altid adgang (hard whitelist)
          if (guestIds.has(m.id)) return true;
          // Resten skal passere checkAccessFor for dette specifikke rum
          return checkAccessFor(m, roomId, accessCtx).hasAccess;
        });
      }
      setAllRoomMembers(visibleMembers);
    };
    loadMembers();
  }, [classroomIds?.join(','), classroomId, roomId]);

  const activeIds = new Set(occupants.map(o => o.id));
  const inactive = allRoomMembers.filter(m => !activeIds.has(m.id));
  const activeGuardians = occupants.filter(o => isPedagogicalStaff(o.role));
  const activeStudents = occupants.filter(o => o.role === 'student');
  const inactiveGuardians = inactive.filter(m => isPedagogicalStaff(m.role));
  const inactiveStudents = inactive.filter(m => m.role === 'student');

  const renderMember = (player: Player, isActive: boolean) => {
    const wizardName = s(player.wizardName || (player as any).username || 'Ukendt');
    // PRESENCE-PRIVACY-V1 (21-9-2026): rigtigt navn og pakke kun for staff (beslutningen 11/8). De aktive kommer fra
    // presence, som ikke har dem mere, saa de tages fra users (staffInfo) eller fra rummets medlemmer.
    const personInfo: any = staffInfo[player.id] || allRoomMembers.find((m) => m.id === player.id) || player;
    const realName = viewerIsStaff ? s(personInfo.realName || '') : '';
    // Vis realName kun hvis det er forskelligt fra wizardName
    const showRealName = realName && realName.toLowerCase() !== wizardName.toLowerCase();
    const isGuardian = isPedagogicalStaff(player.role);

    return (
      <div
        key={player.id}
        onClick={() => { if (isActive) { onPlayerClick(player); onClose(); } }}
        className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
          isActive
            ? 'bg-white/5 border-white/10 hover:border-yellow-500/30 hover:bg-white/10 cursor-pointer'
            : 'bg-black/20 border-white/5'
        }`}
      >
        <div className="relative shrink-0">
          <img
            src={player.avatarUrl || '/default-avatar.png'}
            alt=""
            className={`w-10 h-10 rounded-full object-cover border ${isActive ? 'border-white/30' : 'border-white/10'}`}
          />
          <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#0a0a1a] ${
            isActive ? 'bg-green-400' : 'bg-gray-500'
          }`} />
        </div>

        <div className="flex-1 min-w-0">
  <div className="flex items-center gap-2 flex-wrap">
    {/* Troldmandsnavn — altid vist */}
    <span className={`font-bold text-sm uppercase tracking-wide truncate ${isActive ? 'text-white' : 'text-white/70'}`}>
      {wizardName}
    </span>
    {isGuardian && (
  <span className="text-[7px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded font-black uppercase shrink-0">
    Vogter
  </span>
)}
{!isGuardian && isPedagogicalStaff(currentUser.role) && (personInfo.campusPackage || personInfo.packageType) === 'plus' && (
  <span className="text-[7px] bg-indigo-500/30 text-indigo-300 px-1.5 py-0.5 rounded font-black uppercase shrink-0">
    Plus
  </span>
)}
{!isGuardian && isPedagogicalStaff(currentUser.role) && (personInfo.campusPackage || personInfo.packageType) === 'standard' && (
  <span className="text-[7px] bg-white/10 text-white/50 px-1.5 py-0.5 rounded font-black uppercase shrink-0">
    Standard
  </span>
)}
  </div>
  {/* Rigtigt navn — vises kun hvis det er forskelligt fra troldmandsnavn */}
  {showRealName && (
    <span className={`text-[11px] block mt-0.5 ${isActive ? 'text-white/65' : 'text-white/45'}`}>
      {realName}
    </span>
  )}
</div>
      </div>
    );
  };

  const Section = ({ title, members, isActive }: { title: string; members: Player[]; isActive: boolean }) => {
    if (members.length === 0) return null;
    return (
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-white/55 mb-2">{title}</p>
        <div className="space-y-1.5">
          {members.map(m => renderMember(m, isActive))}
        </div>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg bg-[#0a0a1a]/95 border-2 border-indigo-500/30 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-white/10 bg-indigo-900/20 flex justify-between items-center shrink-0">
          <div>
            <h3 className="text-xl font-cinzel text-yellow-500 tracking-widest uppercase">Rummet</h3>
            <p className="text-[10px] font-black uppercase text-indigo-300 tracking-widest mt-0.5">
              {occupants.length} aktive
              {allRoomMembers.length > 0
                ? ` · ${allRoomMembers.length} tilknyttede`
                : ' · ingen classroom tilknyttet'}
            </p>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white text-4xl transition-colors leading-none">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-5 no-scrollbar">
          <Section title="🟢 Aktive vogtere"          members={activeGuardians}   isActive={true} />
          <Section title="🟢 Aktive eventyrere"        members={activeStudents}    isActive={true} />
          {allRoomMembers.length > 0 && (
            <>
              <Section title="⬜ Fraværende vogtere"    members={inactiveGuardians} isActive={false} />
              <Section title="⬜ Fraværende eventyrere" members={inactiveStudents}  isActive={false} />
            </>
          )}
          {allRoomMembers.length === 0 && inactive.length === 0 && occupants.length === 0 && (
            <p className="text-center text-white/40 italic font-cinzel text-sm py-8">Rummet er tomt...</p>
          )}
        </div>

        <div className="p-4 bg-black/20 text-center shrink-0">
          <p className="text-[10px] text-indigo-400/70 uppercase tracking-[0.2em] italic">
            Klik på en aktiv person for at se deres profil
          </p>
        </div>
      </div>
    </div>
  );
};

export default OccupantList;